import React, { useEffect, useState } from 'react';
import { ModalBody } from 'app/pages/library/videos/videoList/components/SendAndShare';
import { LoadingIndicator } from 'lib/components';
import { useGetMeetingDetailsQuery } from 'lib/api/meetings/useGetMeetingDetailsQuery';
import { MEETING_STATUSES, MeetingStatus } from 'lib/api/meetings/types';
import { SendEmailNotificationsMeetingModal } from 'app/pages/meetings/components/modals/SendEmailNotificationsMeetingModal';
import duration from 'dayjs/plugin/duration';
import { MeetingsFormikDetailsHeader } from '../components/MeetingsDetailsHeader';
import { MeetingDetailsBodyWrapper } from '../styles';
import { getMeetingTabs } from '../constants';
import { MeetingTabChat } from '../meetingTabs/MeetingTabChat';
import { MeetingTabRecording } from '../meetingTabs/MeetingTabRecording';
import { MeetingTabDetails } from '../meetingTabs/MeetingTabDetails';
import { Form, Formik } from 'formik';
import dayjs from 'dayjs';
import { useAuth } from 'lib/context';
import { useTranslation } from 'react-i18next';

import { useGetUsersCustomerQuery } from 'lib/api/users/getUsersCustomers';
import { getDisplayName } from 'lib/utils/functions';
import {
  getCustomDateTimePickerValue,
  getDeliveryTime,
  getDurationHours,
  getDurationMinutes,
  getUserTimezone,
} from '../utils';
import { useUpdateMeetingMutation } from 'lib/api/meetings/useUpdateMeetingMutation';
import { useCreateMeetingMutation } from 'lib/api/meetings/useCreateMeetingMutation';
import { DeleteMeetingModal } from '../components/modals/DeleteMeetingModal';
import { CancelMeetingModal } from '../components/modals/CancelMeetingModal';
import { useSendInviteMeetingMutation } from 'lib/api/meetings/useSendInviteMeetingMutation';
import { FullScreenModal } from 'lib/components/FullScreenModal';
import {
  FormikMeetingsValues,
  ICreateMeetingPayload,
  IUpdateMeetingPayload,
} from '../types';
import { useGTMTrackPageView } from 'lib/hooks/GTM/useGTMTrackPageView';
import { useCurrentAppUrl } from 'lib/hooks/useCurrentAppUrl';
import { checkAttendeeLimit } from 'lib/utils/productFeature';
import { useHistory, useLocation, useParams } from 'react-router';
import {
  getChangedFields,
  mapDeliveryTimeToGtmMeeting,
  TRACKED_PAGES,
} from 'lib/hooks/GTM/utils';
import { useStartNowMeetingMutation } from 'lib/api/meetings/useStartNowMeetingMutation';
import { useGTMAnalytics } from 'lib/hooks/GTM/useGTMAnalytics';
import {
  CreateMeetingEventType,
  GTMMeetingInteractionAction,
} from 'lib/hooks/GTM/actions/meeting/types';

dayjs.extend(duration);

export type ModalMeetingsDetails =
  | 'send-notifications'
  | 'delete'
  | 'cancel'
  | null;

export const MeetingDetails = () => {
  const history = useHistory();
  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const createMeetingStatus = sp.get('status');
  const { t } = useTranslation();

  const { meetingId } = useParams<{ meetingId: string }>();
  const { livePageUrl } = useCurrentAppUrl();
  const MEETING_TABS = getMeetingTabs(t);

  const [activeTab, setActiveTab] = useState(MEETING_TABS.DETAILS.value);
  const { userData } = useAuth();
  const { id: userId } = userData;
  const attendeeLimit = checkAttendeeLimit(userData);

  const { data: meetingDetails, isLoading } = useGetMeetingDetailsQuery(
    meetingId === 'new' ? '' : meetingId || ''
  );

  const { mutateAsync: startNowAsync } = useStartNowMeetingMutation();

  const { trackVideoSpecificPagesManually } = useGTMTrackPageView();
  const { isCompanyAdmin, timezone } = userData;
  const { data: companyUsers = [] } = useGetUsersCustomerQuery(
    {},
    !!isCompanyAdmin
  );
  const isRecurring =
    createMeetingStatus === MEETING_STATUSES.RECURRING ||
    meetingDetails?.meetingStatus === MEETING_STATUSES.RECURRING;

  const [showDetailsModals, setShowDetailsModals] =
    useState<ModalMeetingsDetails>(null);

  const handleCloseAllModals = () => {
    history.push('/meetings');
    setShowDetailsModals(null);
  };

  const { mutateAsync: updateMeetingAsync } =
    useUpdateMeetingMutation(handleCloseAllModals);

  const { mutateAsync: createMeetingAsync } =
    useCreateMeetingMutation(handleCloseAllModals);

  const { mutateAsync: sendInvite } = useSendInviteMeetingMutation();

  const {
    trackGTMMeetingInteractionEvent,
    trackGTMCreateMeetingEvent,
    trackGTMMeetingAttributesChangedEvent,
  } = useGTMAnalytics();

  const onStartNowClick = async () => {
    startNowAsync({
      meetingId,
      userId: Number(userData.id),
    });

    if (meetingDetails) {
      trackGTMMeetingInteractionEvent?.({
        meetingResponse: meetingDetails,
        livePageUrl,
        attendeeLimit,
        action: GTMMeetingInteractionAction.ADDED,
      });
    }
  };

  const meetingAttributesChangedEvent = (values: FormikMeetingsValues) => {
    const { changed_fields, ...rest } = getChangedFields({
      initialValues: INITIAL_FORMIK_VALUES,
      currentValues: {
        ...INITIAL_FORMIK_VALUES,
        ...values,
      },
      trackedFields: [
        'title',
        'description',
        'deliveryTime',
        'preferedUserTimezone.label',
        'userId',
      ],
      trackedArrayFields: { attendees: ['email'] },
    });

    trackGTMMeetingAttributesChangedEvent?.({
      fieldChanges: rest,
      updatedFields: changed_fields,
      meetingDetails,
      action: GTMMeetingInteractionAction.CHANGED,
    });
  };

  const onSubmitHandler = async (values: FormikMeetingsValues) => {
    const minutes = values.durationMinutes.value;
    const hours = values.durationHours.value;
    const duration = hours * 60 + minutes;

    if (meetingId) {
      const payload: IUpdateMeetingPayload = {
        meetingId,
        title: values.title.trim(),
        description: values.description,
        duration,
        userId: values.userId,
        deliveryTime: values.deliveryTime,
        enableGuestScreenShare: values.enableGuestScreenShare,
        hostApproval: values.hostApproval,
        frequency: values.frequency,
        attendees: values.attendees,
      };
      updateMeetingAsync({
        meetingData: payload,
        ...(values.sendEmail ? { sendEmail: true } : {}),
      });
      if (meetingDetails) {
        trackGTMMeetingInteractionEvent?.({
          meetingResponse: meetingDetails,
          livePageUrl,
          attendeeLimit,
          action: GTMMeetingInteractionAction.SAVED_CHANGES,
        });
        meetingAttributesChangedEvent(values);
      }
      return;
    }

    const payload: ICreateMeetingPayload = {
      title: values.title.trim(),
      description: values.description,
      duration,
      userId: values.userId,
      deliveryTime: values.deliveryTime,
      enableGuestScreenShare: values.enableGuestScreenShare,
      hostApproval: values.hostApproval,
      attendees: values.attendees,
      frequency: values.frequency,
      meetingStatus: isRecurring
        ? MEETING_STATUSES.RECURRING
        : MEETING_STATUSES.SCHEDULED,
    };
    const meeting = await createMeetingAsync({ ...payload });

    // T0_DO CRETE NEW MEETING GTM??
    if (meeting?.attendees?.length && meeting.meetingId) {
      await sendInvite(meeting.meetingId);
    }
    trackGTMCreateMeetingEvent?.({
      meetingResponse: meeting,
      livePageUrl,
      attendeeLimit,
      type: CreateMeetingEventType.SCHEDULE,
    });
  };

  useEffect(() => {
    if (meetingId === 'new') {
      trackVideoSpecificPagesManually?.({
        ...TRACKED_PAGES.MEETING_SCHEDULE,
      });
    }
    if (!meetingDetails) {
      return;
    }
    const { meetingDate, meetingTime } = mapDeliveryTimeToGtmMeeting(
      meetingDetails?.deliveryTime
    );

    const meetingUrl = meetingId ? livePageUrl + meetingId : undefined;

    trackVideoSpecificPagesManually?.({
      ...TRACKED_PAGES.MEETING_DETAILS,
      meetingAttributes: {
        meetingId: meetingDetails.meetingId || undefined,
        meetingTitle: meetingDetails.title || undefined,
        invitedParticipants: attendeeLimit,
        meetingUrl: meetingUrl,
        meetingDate: meetingDate,
        meetingTime: meetingTime,
        meetingHost: meetingDetails.host?.id,
        crmParticipants:
          meetingDetails?.attendees.map(attendee =>
            attendee?.meetingAttendeeID?.toString()
          ) || undefined,
      },
    });
  }, [meetingDetails]);

  if (isLoading) {
    return (
      <FullScreenModal>
        <LoadingIndicator isLoading={true} height='300px' />
      </FullScreenModal>
    );
  }

  const INITIAL_FORMIK_VALUES: FormikMeetingsValues = {
    meetingId: meetingDetails?.meetingId || null,
    meetingStatus: meetingDetails?.meetingStatus,
    duration: meetingDetails?.duration || 0,
    attendees:
      meetingDetails?.attendees.map(email => ({ email: email.email })) || [],
    title: meetingDetails?.title?.trim() || '',
    description: meetingDetails?.description || '',
    userId: meetingDetails?.userId || Number(userId),
    deliveryTime:
      meetingDetails?.deliveryTime || getDeliveryTime(meetingDetails, timezone),
    enableGuestScreenShare: meetingDetails?.enableGuestScreenShare || false,
    hostApproval: meetingDetails?.hostApproval || false,
    // formik helpers filed
    durationHours: getDurationHours(meetingDetails),
    durationMinutes: getDurationMinutes(meetingDetails),
    preferedUserTimezone: getUserTimezone(meetingDetails, timezone),
    customDateTimePicker: getCustomDateTimePickerValue(meetingDetails),
    sendEmail: false,
    frequency: meetingDetails?.frequency,
  };

  const userOptions = companyUsers.map(user => ({
    value: user.id.toString(),
    label: getDisplayName([
      user.id.toString() === userData.id.toString() ? `(${t('Me')})` : '',
      user.lastName,
      user.firstName,
      user.email && (user.lastName || user.firstName)
        ? `- ${user.email}`
        : user.email,
    ]),
  }));

  return (
    <FullScreenModal>
      <Formik initialValues={INITIAL_FORMIK_VALUES} onSubmit={onSubmitHandler}>
        {({ handleSubmit }) => {
          return (
            <>
              <MeetingsFormikDetailsHeader
                handleCloseFullScreenModal={() => history.push('/meetings')}
                meetingDetails={meetingDetails}
                meetingId={meetingId}
                setShowDetailsModals={setShowDetailsModals}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isRecurring={isRecurring}
                onStartNowClick={onStartNowClick}
              />

              {MEETING_TABS.DETAILS.value === activeTab && (
                <ModalBody>
                  <Form onSubmit={handleSubmit}>
                    <MeetingTabDetails
                      meetingDetails={meetingDetails}
                      userOptions={userOptions}
                      createMeetingStatus={
                        createMeetingStatus as MeetingStatus | null
                      }
                    />
                    {showDetailsModals === 'send-notifications' && (
                      <SendEmailNotificationsMeetingModal
                        handleModalClose={handleCloseAllModals}
                      />
                    )}
                  </Form>
                </ModalBody>
              )}
              {MEETING_TABS.RECORDING.value === activeTab && (
                <MeetingDetailsBodyWrapper>
                  <MeetingTabRecording meetingId={meetingId} />
                </MeetingDetailsBodyWrapper>
              )}
              {MEETING_TABS.CHAT.value === activeTab && (
                <MeetingDetailsBodyWrapper>
                  <MeetingTabChat meetingId={meetingId} />
                </MeetingDetailsBodyWrapper>
              )}
            </>
          );
        }}
      </Formik>

      {showDetailsModals === 'delete' && meetingId && (
        <DeleteMeetingModal
          meetingId={meetingId}
          handleModalClose={() => setShowDetailsModals(null)}
          handleCloseAllModals={handleCloseAllModals}
        />
      )}

      {showDetailsModals === 'cancel' && meetingId && (
        <CancelMeetingModal
          attendees={meetingDetails?.attendees || []}
          meetingId={meetingId}
          handleModalClose={() => setShowDetailsModals(null)}
          handleCloseAllModals={handleCloseAllModals}
        />
      )}
    </FullScreenModal>
  );
};
